<script>
import { mapActions, mapState } from 'vuex';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons-vue';
import ArticleDataTab from '@/pages/article/articleData/Article-data-tab.vue';
import ArticleEditorTab from '@/pages/article/articleEditor/Article-editor-tab.vue';
import ArticleAdsBanner from '@/pages/article/articleAdsBanner/Article-ads-banner.vue';
import ArticleBottomAdsBanner from '@/pages/article/articleBottomAdsBanner/Article-bottom-ads-banner.vue';
import pageUrls from '@/router/pageUrls';
import bem from '@/utils/bem';

const { blogsPage } = pageUrls;
const b = bem('article-page');

// Required text fields for validation
const REQUIRED_FIELDS = ['cardTitle', 'title', 'descriptionSeo', 'titleSeo', 'author', 'categoryId', 'subTitle'];
const REQUIRED_IMAGES = ['desktopImage', 'mobileImage', 'previewImage'];

export default {
  components: {
    ArticleDataTab,
    ArticleEditorTab,
    ArticleAdsBanner,
    ArticleBottomAdsBanner,
    ArrowLeftOutlined,
    SaveOutlined,
  },
  data() {
    return {
      isNew: true,
      activeKey: 'articleData',

      // All form state lives here so tab switches don't lose data
      formData: {
        cardTitle: '',
        title: '',
        descriptionSeo: '',
        titleSeo: '',
        author: '',
        categoryId: null,
        subTitle: '',
        version: 4, // default to 'initial' type
      },
      body: '',
      initialBody: '', // passed to editor once on load, separate from reactive body
      isPublished: false,
      isFeatured: false,
      desktopImage: [],
      mobileImage: [],
      previewImage: [],

      // Validation errors keyed by field name
      validationErrors: {},
      isSaving: false,
      bannersLoaded: false,

      // Destructive save warning — prevents accidental loss of custom HTML
      showDestructiveWarning: false,
      destructiveStats: { charDiff: 0, percentReduction: 0 },
      pendingSavePayload: null,
    };
  },
  computed: {
    ...mapState({
      currentArticle: (state) => state.blogs.currentArticle,
      isCurrentArticleLoaded: (state) => state.blogs.isCurrentArticleLoaded,
      categoriesList: (state) => state.blogs.categoriesList,
      isLoading: (state) => state.blogs.isLoading,
    }),
    titlePage() {
      return this.isNew ? 'Create Article' : 'Edit Article';
    },
    isDataLoaded() {
      // Only wait for the article itself, not for banner loading which shares isLoading flag
      return this.isNew || this.isCurrentArticleLoaded;
    },
  },
  methods: {
    ...mapActions({
      getArticleAction: 'blogs/getArticle',
      getArticleBannerAdsAction: 'blogs/getArticleBannerAds',
      getCategoriesAction: 'blogs/getCategories',
      getAllBannerAdsAction: 'blogs/getAllBannerAds',
      createOrUpdateArticleAction: 'blogs/createOrUpdateArticle',
    }),
    /* Подгружаем баннеры только при переключении на таб баннеров */
    onTabChange(key) {
      this.activeKey = key;
      if ((key === 'adsData' || key === 'bottomAdsData') && !this.bannersLoaded) {
        this.bannersLoaded = true;
        this.getAllBannerAdsAction();
      }
    },
    b(el, mods) { return b(el, mods); },

    goBack() {
      this.$router.push(`${blogsPage}/articles`);
    },

    loadArticle(id) {
      const articleId = id || this.$route.params?.id;
      if (articleId) {
        this.isNew = false;
        this.bannersLoaded = false;
        this.getArticleAction(articleId);
        this.getArticleBannerAdsAction(articleId);
      } else {
        this.isNew = true;
      }
    },

    // Populate form from loaded article data
    setFromArticle(article) {
      if (!article?.id) return;
      this.formData = {
        cardTitle: article.cardTitle || '',
        title: article.title || '',
        descriptionSeo: article.descriptionSeo || '',
        titleSeo: article.titleSeo || '',
        author: article.author || '',
        categoryId: article.categoryId || null,
        subTitle: article.subTitle || '',
        version: article.version || 4,
      };
      this.body = article.body || '';
      this.initialBody = article.body || '';
      this.isPublished = article.isPublished || false;
      this.isFeatured = article.isFeatured || false;
    },

    // --- Validation ---
    validate() {
      const errors = {};

      // Required text fields
      REQUIRED_FIELDS.forEach((field) => {
        const val = this.formData[field];
        if (!val || (typeof val === 'string' && !val.trim())) {
          errors[field] = true;
        }
      });

      // Required images — check if file list has at least one item with a file or url
      REQUIRED_IMAGES.forEach((imgKey) => {
        const list = this[imgKey];
        const hasImage = list?.length > 0 && (list[0]?.file || list[0]?.url);
        if (!hasImage) {
          errors[imgKey] = true;
        }
      });

      // Body (editor content)
      if (!this.body || !this.body.trim()) {
        errors.body = true;
      }

      this.validationErrors = errors;
      return Object.keys(errors).length === 0;
    },

    // --- Save ---
    async handleSave() {
      if (this.isSaving) return;

      if (!this.validate()) {
        // Determine which tab to switch to based on errors
        const hasDataErrors = REQUIRED_FIELDS.some((f) => this.validationErrors[f])
          || REQUIRED_IMAGES.some((f) => this.validationErrors[f]);

        if (hasDataErrors) {
          this.activeKey = 'articleData';
        } else if (this.validationErrors.body) {
          this.activeKey = 'editor';
        }
        return;
      }

      // Check for significant content loss before saving — protects custom HTML
      // injected via API (review cards, ad blocks, styled divs) from being
      // silently stripped by the editor's ProseMirror schema
      const originalLength = (this.initialBody || '').length;
      const currentLength = (this.body || '').length;
      if (!this.isNew && originalLength > 0) {
        const charDiff = originalLength - currentLength;
        const percentReduction = (charDiff / originalLength) * 100;

        if (percentReduction > 10 || charDiff > 1000) {
          this.destructiveStats = {
            charDiff,
            percentReduction: Math.round(percentReduction),
          };
          this.showDestructiveWarning = true;
          return;
        }
      }

      this.executeSave(false);
    },

    // skipBody: when true, saves metadata only without overwriting article body
    async executeSave(skipBody) {
      this.showDestructiveWarning = false;
      this.isSaving = true;

      // Sanitize URL slug before saving — remove invalid chars, collapse hyphens
      const titleSeo = this.formData.titleSeo
        .replace(/\s+/g, '-')
        .replace(/[^A-Za-z0-9-]/g, '')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');

      const values = {
        ...this.formData,
        titleSeo,
        categoryId: Number(this.formData.categoryId),
        isPublished: this.isPublished,
        isFeatured: this.isFeatured,
        editorVersion: 'tiptap',
      };

      if (!skipBody) {
        values.body = this.body;
      }

      const images = [
        this.desktopImage?.[0] ?? {},
        this.mobileImage?.[0] ?? {},
        this.previewImage?.[0] ?? {},
      ];

      try {
        await this.createOrUpdateArticleAction({
          isNew: this.isNew,
          id: this.currentArticle?.id,
          values,
          images,
          callBack: this.goBack,
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
  mounted() {
    this.loadArticle();
    this.getCategoriesAction();
  },
  watch: {
    // Reload article when route param changes (component reuse on navigation)
    '$route.params.id': {
      handler(newId) {
        this.loadArticle(newId);
      },
    },
    currentArticle: {
      handler(newVal, oldVal) {
        if (newVal?.id && newVal.id !== oldVal?.id) {
          this.setFromArticle(newVal);
        }
      },
      deep: true,
    },
    // Clear validation errors reactively when fields are filled
    formData: {
      handler(newVal) {
        REQUIRED_FIELDS.forEach((field) => {
          if (this.validationErrors[field] && newVal[field] && String(newVal[field]).trim()) {
            const updated = { ...this.validationErrors };
            delete updated[field];
            this.validationErrors = updated;
          }
        });
      },
      deep: true,
    },
    desktopImage(val) {
      if (this.validationErrors.desktopImage && val?.length > 0) {
        const updated = { ...this.validationErrors };
        delete updated.desktopImage;
        this.validationErrors = updated;
      }
    },
    mobileImage(val) {
      if (this.validationErrors.mobileImage && val?.length > 0) {
        const updated = { ...this.validationErrors };
        delete updated.mobileImage;
        this.validationErrors = updated;
      }
    },
    previewImage(val) {
      if (this.validationErrors.previewImage && val?.length > 0) {
        const updated = { ...this.validationErrors };
        delete updated.previewImage;
        this.validationErrors = updated;
      }
    },
    body(val) {
      if (this.validationErrors.body && val?.trim()) {
        const updated = { ...this.validationErrors };
        delete updated.body;
        this.validationErrors = updated;
      }
    },
  },
};
</script>

<template>
  <!-- Loading spinner for edit mode -->
  <div v-if="!isDataLoaded" :class="b('loading')">
    <a-spin size="large" />
  </div>

  <div v-else :class="b()">
    <!-- Header -->
    <div :class="b('header')">
      <div :class="b('header-left')">
        <a-button type="text" :class="b('back-btn')" @click="goBack">
          <ArrowLeftOutlined />
          <span>Back</span>
        </a-button>
        <h1 :class="b('title')">{{ titlePage }}</h1>
      </div>
      <a-button
        type="primary"
        :class="b('save-btn')"
        :loading="isSaving"
        @click="handleSave"
      >
        <SaveOutlined v-if="!isSaving" />
        {{ isNew ? 'Create' : 'Save' }}
      </a-button>
    </div>

    <!-- Tabs -->
    <a-tabs
      :activeKey="activeKey"
      type="card"
      :class="b('tabs')"
      @change="onTabChange"
    >
      <a-tab-pane key="articleData" tab="Article data">
        <ArticleDataTab
          :formData="formData"
          @update:formData="formData = $event"
          :isPublished="isPublished"
          @update:isPublished="isPublished = $event"
          :isFeatured="isFeatured"
          @update:isFeatured="isFeatured = $event"
          :desktopImage="desktopImage"
          @update:desktopImage="desktopImage = $event"
          :mobileImage="mobileImage"
          @update:mobileImage="mobileImage = $event"
          :previewImage="previewImage"
          @update:previewImage="previewImage = $event"
          :currentArticle="currentArticle || {}"
          :categoriesList="categoriesList || []"
          :validationErrors="validationErrors"
          :isNew="isNew"
        />
      </a-tab-pane>

      <a-tab-pane key="editor" tab="Editor">
        <ArticleEditorTab
          :body="body"
          :initialBody="initialBody"
          @update:body="body = $event"
          :hasError="!!validationErrors.body"
        />
      </a-tab-pane>

      <a-tab-pane
        key="adsData"
        :tab="isNew ? 'Side banner' : 'Side banner'"
        :disabled="isNew"
      >
        <article-ads-banner />
      </a-tab-pane>

      <a-tab-pane
        key="bottomAdsData"
        :tab="isNew ? 'Bottom banner' : 'Bottom banner'"
        :disabled="isNew"
      >
        <article-bottom-ads-banner />
      </a-tab-pane>
    </a-tabs>

    <!-- Destructive Save Warning — shown when editor content is significantly smaller
         than the original API body, indicating possible custom HTML loss -->
    <a-modal
      v-model:open="showDestructiveWarning"
      title="Warning: Content Size Reduced"
      :footer="null"
      @cancel="showDestructiveWarning = false"
    >
      <p>
        The article body has been reduced by
        <strong>{{ destructiveStats.charDiff }}</strong> characters
        (<strong>{{ destructiveStats.percentReduction }}%</strong>).
        This may indicate that custom HTML content (review cards, ads, styled blocks)
        was stripped by the editor.
      </p>
      <div style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px;">
        <a-button @click="showDestructiveWarning = false">Cancel</a-button>
        <a-button @click="executeSave(true)">Save without body</a-button>
        <a-button type="primary" danger @click="executeSave(false)">Save anyway</a-button>
      </div>
    </a-modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/media';

.article-page {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 90vh;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    border-bottom: 1px solid var(--Border-Light, #e8e8e8);
    background: var(--Background-Main, #fff);
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__back-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--Text-Secondary, #6b7280);
    font-size: 14px;
    padding: 4px 8px;

    &:hover {
      color: var(--Primary, #5b8fa3);
    }
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: var(--Text-Primary, #2b3544);
  }

  &__save-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 20px;
    font-weight: 500;
  }

  &__tabs {
    flex: 1;
    display: flex;
    flex-direction: column;

    :deep(.ant-tabs-content) {
      flex: 1;
    }

    :deep(.ant-tabs-tabpane) {
      height: 100%;
    }
  }
}
</style>
